import React, { useState, useEffect } from "react";
import { PlusCircle, Save, Trash2 } from "lucide-react";
import axios from "axios";
import Header from "./Header";
import VertNav from "./VertNav";
import config from "../../config";

function InventorySetting() {
    const [rows, setRows] = useState([
        {
            service: "",
            product: "",
            usage_amount: "", // Changed from consumption_per_service to usage_amount
        },
    ]);
    const [services, setServices] = useState([]);
    const [products, setProducts] = useState([]);
    const [servicesLoading, setServicesLoading] = useState(false);
    const [productsLoading, setProductsLoading] = useState(false);

    const bid = localStorage.getItem("branch_id");
    const token = localStorage.getItem("token");

    useEffect(() => {
        fetchServices();
        fetchProducts();
        fetchRules();
    }, []);

    const fetchRules = async () => {
        try {
            const response = await axios.get(
                `${config.apiUrl}/api/swalook/inventory/service-usage/?branch_name=${bid}`,
                {
                    headers: {
                        Authorization: `Token ${token}`,
                        "Content-Type": "application/json",
                    },
                },
            );

            if (
                response.data &&
                response.data.data &&
                response.data.data.length > 0
            ) {
                const fetchedRows = response.data.data.map((rule) => ({
                    id: rule.id,
                    service: rule.service ? rule.service.toString() : "",
                    product: rule.product ? rule.product.toString() : "",
                    usage_amount: rule.usage_amount
                        ? rule.usage_amount.toString()
                        : "",
                }));
                setRows(fetchedRows);
            }
        } catch (error) {
            console.error("Error fetching rules:", error);
        }
    };

    // Fetch all services (including combos)
    const fetchServices = async () => {
        setServicesLoading(true);
        try {
            const response = await axios.get(
                `${config.apiUrl}/api/swalook/table/services/?branch_name=${bid}`,
                {
                    headers: {
                        Authorization: `Token ${token}`,
                        "Content-Type": "application/json",
                    },
                },
            );

            const allServices = [];
            response.data.data.forEach((category) => {
                if (category.services && category.services.length > 0) {
                    category.services.forEach((service) => {
                        allServices.push({
                            id: service.id,
                            name: service.service,
                            price: service.service_price,
                            duration: service.service_duration,
                            category: category.category_name,
                            for_men: service.for_men,
                            for_women: service.for_women,
                        });
                    });
                }

                // Include combos as services
                if (category.combos && category.combos.length > 0) {
                    category.combos.forEach((combo) => {
                        allServices.push({
                            id: combo.id || `combo_${combo.combo_name}`,
                            name: `${combo.combo_name} (Combo)`,
                            price: combo.combo_price,
                            duration: combo.duration,
                            category: category.category_name,
                            isCombo: true,
                        });
                    });
                }
            });

            setServices(allServices);
        } catch (error) {
            console.error("Error fetching services:", error);
        } finally {
            setServicesLoading(false);
        }
    };

    // Fetch all products
    const fetchProducts = async () => {
        setProductsLoading(true);
        try {
            const response = await axios.get(
                `${config.apiUrl}/api/swalook/inventory/product/?branch_name=${bid}`,
                {
                    headers: {
                        Authorization: `Token ${token}`,
                        "Content-Type": "application/json",
                    },
                },
            );

            if (response.data && response.data.data) {
                setProducts(response.data.data);
            }
        } catch (error) {
            console.error("Error fetching products:", error);
        } finally {
            setProductsLoading(false);
        }
    };

    const handleChange = (index, field, value) => {
        const updated = [...rows];
        updated[index][field] = value;
        setRows(updated);
    };

    const addRow = () => {
        setRows([
            ...rows,
            {
                service: "",
                product: "",
                usage_amount: "", // Changed from consumption_per_service to usage_amount
            },
        ]);
    };

    const removeRow = async (index) => {
        const row = rows[index];
        if (row.id) {
            try {
                await axios.delete(
                    `${config.apiUrl}/api/swalook/inventory/service-usage/?branch_name=${bid}&id=${row.id}`,
                    {
                        headers: {
                            Authorization: `Token ${token}`,
                            "Content-Type": "application/json",
                        },
                    },
                );
            } catch (error) {
                console.error("Error deleting rule:", error);
                alert("Error deleting rule from server.");
                return;
            }
        }

        const updated = rows.filter((_, i) => i !== index);
        setRows(
            updated.length
                ? updated
                : [
                      {
                          service: "",
                          product: "",
                          usage_amount: "",
                      },
                  ],
        );
    };

    const handleSave = async () => {
        // Validate all rows
        const invalidRows = rows.filter((row) => {
            if (
                !row.service ||
                !row.product ||
                !row.usage_amount || // Changed from consumption_per_service to usage_amount
                parseFloat(row.usage_amount) <= 0 // Changed from consumption_per_service to usage_amount
            ) {
                return true;
            }
            return false;
        });

        if (invalidRows.length > 0) {
            alert("Please fill all required fields with valid values.");
            return;
        }

        try {
            // Save each rule individually
            const savePromises = rows.map(async (row) => {
                const payload = {
                    service: row.service,
                    product: row.product,
                    usage_amount: parseFloat(row.usage_amount), // Changed from consumption_per_service to usage_amount
                };

                console.log("Saving payload:", payload); // Debug log

                // If rule has an id, use PUT to update it, otherwise POST
                const method = row.id ? "put" : "post";
                const url = row.id
                    ? `${config.apiUrl}/api/swalook/inventory/service-usage/?branch_name=${bid}&id=${row.id}`
                    : `${config.apiUrl}/api/swalook/inventory/service-usage/?branch_name=${bid}`;

                const response = await axios[method](url, payload, {
                    headers: {
                        Authorization: `Token ${token}`,
                        "Content-Type": "application/json",
                    },
                });

                return response.data;
            });

            const results = await Promise.all(savePromises);

            // Check if all saves were successful
            const allSuccess = results.every(
                (result) => result.status === true,
            );

            if (allSuccess) {
                alert(`${rows.length} consumption rule(s) saved successfully!`);
                fetchRules(); // Reload rules so newly created ones receive their IDs
            } else {
                const failedCount = results.filter(
                    (result) => result.status !== true,
                ).length;
                alert(
                    `${failedCount} out of ${rows.length} rules failed to save. Please check the data and try again.`,
                );
            }
        } catch (error) {
            console.error("Error saving consumption rules:", error);
            if (error.response) {
                console.error("Error response:", error.response.data); // Debug log
                alert(
                    `Error: ${
                        error.response.data.message ||
                        error.response.statusText ||
                        JSON.stringify(error.response.data.errors)
                    }`,
                );
            } else {
                alert("Error saving rules. Please try again.");
            }
        }
    };

    return (
        <div className="bg-gray-50">
            <Header />
            <VertNav />

            <div className="md:ml-72 p-6">
                <div className="bg-white rounded-2xl shadow-md p-8 max-w-screen mx-auto">
                    {/* Page Header */}
                    <div className="mb-6">
                        <h2 className="text-2xl font-bold text-gray-800">
                            Service-Based Inventory Consumption Rules
                        </h2>
                        <p className="text-sm text-gray-500 mt-1">
                            Define product consumption for each service
                        </p>
                    </div>

                    {/* Table */}
                    <div className="overflow-x-auto border border-gray-200 rounded-xl">
                        <table className="min-w-full border-collapse">
                            <thead className="bg-gray-100">
                                <tr>
                                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase w-16">
                                        #
                                    </th>
                                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">
                                        Service
                                    </th>
                                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">
                                        Product
                                    </th>
                                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">
                                        Usage Amount
                                    </th>
                                    <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase w-24">
                                        Action
                                    </th>
                                </tr>
                            </thead>

                            <tbody className="bg-white divide-y divide-gray-200">
                                {rows.map((row, index) => (
                                    <tr
                                        key={index}
                                        className="hover:bg-gray-50"
                                    >
                                        {/* Row Number */}
                                        <td className="px-4 py-2 text-gray-500 font-medium">
                                            {index + 1}
                                        </td>

                                        {/* Service Dropdown */}
                                        <td className="px-4 py-2">
                                            <select
                                                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 text-sm"
                                                value={row.service}
                                                onChange={(e) =>
                                                    handleChange(
                                                        index,
                                                        "service",
                                                        e.target.value,
                                                    )
                                                }
                                                disabled={servicesLoading}
                                            >
                                                <option value="">
                                                    {servicesLoading
                                                        ? "Loading services..."
                                                        : "Select Service"}
                                                </option>
                                                {services.map((service) => (
                                                    <option
                                                        key={service.id}
                                                        value={service.id}
                                                    >
                                                        {service.name}
                                                        {service.category
                                                            ? ` (${service.category})`
                                                            : ""}
                                                        {service.isCombo
                                                            ? " - COMBO"
                                                            : ""}
                                                    </option>
                                                ))}
                                            </select>
                                        </td>

                                        {/* Product Dropdown */}
                                        <td className="px-4 py-2">
                                            <select
                                                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 text-sm"
                                                value={row.product}
                                                onChange={(e) =>
                                                    handleChange(
                                                        index,
                                                        "product",
                                                        e.target.value,
                                                    )
                                                }
                                                disabled={productsLoading}
                                            >
                                                <option value="">
                                                    {productsLoading
                                                        ? "Loading products..."
                                                        : "Select Product"}
                                                </option>
                                                {products.map((product) => (
                                                    <option
                                                        key={product.id}
                                                        value={product.id}
                                                    >
                                                        {product.product_name}
                                                        {product
                                                            .category_details
                                                            ?.product_category
                                                            ? ` (${product.category_details.product_category})`
                                                            : ""}{" "}
                                                        -
                                                        {product.stocks_in_hand}{" "}
                                                        {product.unit}
                                                    </option>
                                                ))}
                                            </select>
                                        </td>

                                        {/* Usage Amount */}
                                        <td className="px-4 py-2">
                                            <div className="flex items-center">
                                                <input
                                                    type="number"
                                                    step="0.01"
                                                    min="0.01"
                                                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 text-sm"
                                                    placeholder={`Amount in ${
                                                        products.find(
                                                            (p) =>
                                                                p.id ===
                                                                row.product,
                                                        )?.unit || "unit"
                                                    }`}
                                                    value={row.usage_amount}
                                                    onChange={(e) =>
                                                        handleChange(
                                                            index,
                                                            "usage_amount",
                                                            e.target.value,
                                                        )
                                                    }
                                                />
                                                {row.product && (
                                                    <span className="ml-2 text-sm font-medium text-gray-700 min-w-16">
                                                        {
                                                            products.find(
                                                                (p) =>
                                                                    p.id ===
                                                                    row.product,
                                                            )?.unit
                                                        }
                                                    </span>
                                                )}
                                            </div>
                                        </td>

                                        {/* Action */}
                                        <td className="px-4 py-2 text-center">
                                            <button
                                                onClick={() => removeRow(index)}
                                                className="text-red-500 hover:text-red-700 p-1.5 rounded-full hover:bg-red-50 transition-colors"
                                                title="Remove rule"
                                            >
                                                <Trash2 size={18} />
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    {/* Controls */}
                    <div className="flex justify-between items-center mt-6">
                        <button
                            onClick={addRow}
                            className="flex items-center gap-2 text-blue-600 hover:text-blue-800 font-medium hover:bg-blue-50 px-4 py-2 rounded-lg transition-colors"
                        >
                            <PlusCircle size={20} />
                            Add New Rule
                        </button>

                        <div className="flex items-center gap-4">
                            <div className="text-sm text-gray-600 bg-gray-100 px-3 py-2 rounded-lg">
                                <span className="font-medium text-gray-800">
                                    {rows.length}
                                </span>{" "}
                                rule(s) configured
                            </div>
                            <button
                                onClick={handleSave}
                                disabled={servicesLoading || productsLoading}
                                className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white font-medium py-3 px-12 rounded-lg shadow-md transition-all disabled:cursor-not-allowed"
                            >
                                <Save size={20} />
                                Save Rules
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default InventorySetting;
